interface ChartPoint {
  label: string;
  value: number;
}

interface AiResultChartProps {
  title: string;
  chartType: 'bar' | 'pie' | 'stat';
  data: ChartPoint[];
}

const CHART_COLORS = [
  'hsl(var(--primary))',
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
];

export function AiResultChart({ title, chartType, data }: AiResultChartProps) {
  if (!data.length) return null;

  const maxValue = Math.max(...data.map((item) => item.value), 1);
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (chartType === 'stat') {
    return (
      <div className="rounded-lg border bg-muted/20 p-4">
        <p className="text-sm font-semibold text-muted-foreground">{title}</p>
        <p className="mt-2 text-4xl font-bold text-primary">{data[0]?.value ?? 0}</p>
        <p className="text-sm text-muted-foreground">{data[0]?.label}</p>
      </div>
    );
  }

  if (chartType === 'pie' && data.length <= 8) {
    let offset = 0;
    const segments = data.map((item, index) => {
      const share = (item.value / total) * 100;
      const segment = `${CHART_COLORS[index % CHART_COLORS.length]} ${offset}% ${offset + share}%`;
      offset += share;
      return segment;
    });

    return (
      <div className="rounded-lg border bg-muted/20 p-4">
        <p className="mb-3 text-sm font-semibold text-muted-foreground">{title}</p>
        <div className="grid gap-4 md:grid-cols-[180px_1fr] md:items-center">
          <div
            className="mx-auto h-36 w-36 rounded-full border"
            style={{ background: `conic-gradient(${segments.join(', ')})` }}
          />
          <div className="space-y-2">
            {data.map((item, index) => (
              <div key={item.label} className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span>{item.label}</span>
                </div>
                <span className="font-medium">
                  {item.value} ({Math.round((item.value / total) * 100)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-muted/20 p-4">
      <p className="mb-3 text-sm font-semibold text-muted-foreground">{title}</p>
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={`${item.label}-${index}`} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="truncate pr-3">{item.label}</span>
              <span className="font-medium">{item.value}</span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${(item.value / maxValue) * 100}%`,
                  backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
